// Admin "all users" table — lists every user's grand total for the
// selected period (derived from the org-wide document's byUser map via
// summarizeByUser()). Row click drills into that user's own breakdown.
import { Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { ExpandableTable } from '../ExpandableTable';
import type { Column } from '../ExpandableTable';
import type { UserUsageSummary } from '../../types/tokenusage';
import { formatCount } from './formatUsage';

const useStyles = makeStyles({
  hint: { color: tokens.colorNeutralForeground3 },
});

export interface AllUsersTableProps {
  users: UserUsageSummary[];
  loading?: boolean;
  onSelectUser?: (oid: string) => void;
}

export function AllUsersTable(props: AllUsersTableProps) {
  const { users, loading, onSelectUser } = props;
  const styles = useStyles();

  const columns: Column<UserUsageSummary>[] = [
    { key: 'userOid', label: 'User OID', render: (r) => r.userOid, sortable: true, filterable: true, getValue: (r) => r.userOid },
    {
      key: 'totalTokens',
      label: 'Tokens',
      render: (r) => formatCount(r.grandTotal.totalTokens),
      sortable: true,
      getValue: (r) => String(r.grandTotal.totalTokens).padStart(20, '0'),
    },
    {
      key: 'requests',
      label: 'Requests',
      render: (r) => formatCount(r.grandTotal.requests),
      sortable: true,
      getValue: (r) => String(r.grandTotal.requests).padStart(20, '0'),
    },
  ];

  return (
    <ExpandableTable<UserUsageSummary>
      columns={columns}
      items={users}
      getRowKey={(r) => r.userOid}
      loading={loading}
      emptyMessage="No users recorded usage for this period."
      searchPlaceholder="Search by user OID…"
      defaultSortKey="totalTokens"
      defaultSortDir="desc"
      onRowClick={onSelectUser ? (r) => onSelectUser(r.userOid) : undefined}
      renderExpanded={(row) => (
        <Caption1 className={styles.hint}>
          Click the row to load {row.userOid}&apos;s own model/day breakdown above.
        </Caption1>
      )}
    />
  );
}

export default AllUsersTable;
